import { useEffect, useState } from 'react'
import { useTranslation } from '@kostbar/i18n'
import { scopeLabelKey } from '../lib/scopes'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

const MAX_NAME_LENGTH = 128

interface CreateApiClientDialogProps {
  open: boolean
  availableScopes: Array<string>
  pending: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (body: { name: string; scopes: Array<string> }) => void
}

export function CreateApiClientDialog({
  open,
  availableScopes,
  pending,
  onOpenChange,
  onSubmit,
}: CreateApiClientDialogProps) {
  const { t } = useTranslation('integrations')
  const [name, setName] = useState('')
  const [scopes, setScopes] = useState<Array<string>>([])

  // Fresh form per opening; every known scope preselected — today there is exactly one.
  useEffect(() => {
    if (!open) return
    setName('')
    setScopes(availableScopes)
  }, [open, availableScopes])

  const trimmed = name.trim()
  const valid = trimmed.length > 0 && scopes.length > 0

  function toggle(scope: string, checked: boolean) {
    setScopes((prev) =>
      checked
        ? [...new Set([...prev, scope])]
        : prev.filter((s) => s !== scope),
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault()
            if (valid && !pending) onSubmit({ name: trimmed, scopes })
          }}
        >
          <DialogHeader>
            <DialogTitle>{t('create.title')}</DialogTitle>
            <DialogDescription>{t('create.description')}</DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-2">
            <Label htmlFor="api-client-name">{t('create.nameLabel')}</Label>
            <Input
              id="api-client-name"
              value={name}
              maxLength={MAX_NAME_LENGTH}
              placeholder={t('create.namePlaceholder')}
              autoComplete="off"
              autoFocus
              onChange={(event) => setName(event.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              {t('create.nameHint')}
            </p>
          </div>

          <fieldset className="flex flex-col gap-2">
            <legend className="mb-1 text-sm font-medium">
              {t('create.scopesLabel')}
            </legend>
            {availableScopes.map((scope) => {
              const id = `scope-${scope.replace(/[^a-z0-9]+/gi, '-')}`
              return (
                <div key={scope} className="flex items-start gap-2">
                  <Checkbox
                    id={id}
                    checked={scopes.includes(scope)}
                    onCheckedChange={(checked) =>
                      toggle(scope, checked === true)
                    }
                  />
                  <Label
                    htmlFor={id}
                    className="flex flex-col items-start gap-0.5"
                  >
                    <span>
                      {t(scopeLabelKey(scope), { defaultValue: scope })}
                    </span>
                    <code className="text-xs font-normal text-muted-foreground">
                      {scope}
                    </code>
                  </Label>
                </div>
              )
            })}
          </fieldset>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              {t('create.cancel')}
            </Button>
            <Button type="submit" disabled={!valid || pending}>
              {pending ? t('create.submitting') : t('create.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
